"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import EditableText from "../../../core/EditableText";
import { LocationContent, buildFullAddress, generateMapEmbedUrl } from "../types";
import { PiMapPin, PiPhone, PiEnvelope, PiNavigationArrow, PiArrowRight, PiLink } from "react-icons/pi";

// ============================================================
// VARIANT 1: FULL-WIDTH LOCATION
// Hero-style map with info bar below
// ============================================================

interface FullWidthLocationProps extends Omit<SectionContentProps, "content"> {
  content: LocationContent;
}

export default function FullWidthLocation({ content: propContent, onContentChange, isSelected }: FullWidthLocationProps) {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Build address and map URL
  const fullAddress = buildFullAddress(content);
  const mapEmbedUrl = content.mapEmbedUrl || generateMapEmbedUrl(fullAddress, content.mapProvider || "google");

  // Handle get directions
  const handleGetDirections = () => {
    const destination = encodeURIComponent(fullAddress);
    window.open(`https://www.google.com/maps/dir/?destination=${destination}`, "_blank");
  };

  // Handle map URL edit
  const handleMapUrlEdit = () => {
    const url = prompt("Enter Google Maps Embed URL:", mapEmbedUrl);
    if (url) onContentChange?.({ ...content, mapEmbedUrl: url });
  };

  return (
    <section className="location-section-full-width" aria-label="Location information">
      {/* Map Container - Full Width */}
      <div className="relative w-full h-[350px] md:h-[450px] bg-slate-100">
        {fullAddress || content.mapEmbedUrl ? (
          <>
            <iframe
              src={mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Map showing our location"
              aria-label="Interactive map"
            />
            {/* Edit Map URL Button */}
            {isSelected && onContentChange && (
              <button
                onClick={handleMapUrlEdit}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-1"
              >
                <PiLink className="w-3 h-3" />
                Edit Map URL
              </button>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100">
            <PiMapPin className="w-12 h-12 text-slate-400 mb-4" />
            <p className="text-slate-500 mb-4">Add your address to display the map</p>
            {isSelected && onContentChange && (
              <button
                onClick={() => {
                  const addr = prompt("Enter full address (Street, City, State, Country):");
                  if (addr) {
                    const parts = addr.split(",").map((s) => s.trim());
                    onContentChange?.({
                      ...content,
                      address: parts[0] || "",
                      city: parts[1] || "",
                      state: parts[2] || "",
                      country: parts[3] || "",
                    });
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Address
              </button>
            )}
          </div>
        )}

        {/* Overlay Header */}
        {(content.title || content.subtitle) && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 via-slate-900/70 to-transparent p-6 md:p-8">
            <div className="max-w-7xl mx-auto">
              {content.title && (
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  <EditableText
                    value={content.title}
                    onChange={(v) => onContentChange?.({ ...content, title: v })}
                    as="span"
                    placeholder="Title"
                  />
                </h2>
              )}
              {content.subtitle && (
                <p className="text-slate-200 text-sm md:text-base">
                  <EditableText
                    value={content.subtitle}
                    onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
                    as="span"
                    placeholder="Subtitle"
                  />
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Info Bar */}
      <div className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            {/* Address */}
            {fullAddress && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <PiMapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide font-medium">Address</p>
                  <p className="text-slate-900 text-sm font-medium">
                    <EditableText
                      value={fullAddress}
                      onChange={(v) => {
                        const parts = v.split(",").map((s) => s.trim());
                        onContentChange?.({
                          ...content,
                          address: parts[0] || "",
                          city: parts[1] || "",
                          state: parts[2] || "",
                          postalCode: parts[3] || "",
                          country: parts[4] || "",
                        });
                      }}
                      as="span"
                      placeholder="Full address"
                    />
                  </p>
                </div>
              </div>
            )}

            {/* Phone */}
            {content.showPhone !== false && content.phone && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <PiPhone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide font-medium">Phone</p>
                  <a
                    href={`tel:${content.phone}`}
                    className="text-slate-900 text-sm font-medium hover:text-blue-600 transition-colors"
                  >
                    <EditableText
                      value={content.phone}
                      onChange={(v) => onContentChange?.({ ...content, phone: v })}
                      as="span"
                      placeholder="Phone number"
                    />
                  </a>
                </div>
              </div>
            )}

            {/* Email */}
            {content.showEmail !== false && content.email && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <PiEnvelope className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide font-medium">Email</p>
                  <a
                    href={`mailto:${content.email}`}
                    className="text-slate-900 text-sm font-medium hover:text-blue-600 transition-colors"
                  >
                    <EditableText
                      value={content.email}
                      onChange={(v) => onContentChange?.({ ...content, email: v })}
                      as="span"
                      placeholder="Email address"
                    />
                  </a>
                </div>
              </div>
            )}

            {/* Get Directions CTA */}
            {content.showGetDirections !== false && (fullAddress || content.ctaLink) && (
              <button
                onClick={() => {
                  if (content.ctaLink) {
                    window.open(content.ctaLink, "_blank");
                  } else {
                    handleGetDirections();
                  }
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <PiNavigationArrow className="w-4 h-4" />
                {content.ctaText || "Get Directions"}
                <PiArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
